<template>
  <div ref="chartRef" :style="{ width: width, height: height }"></div>
</template>

<script>
import { ref, onMounted, watch, onBeforeUnmount } from 'vue';
import * as echarts from 'echarts';

export default {
  name: 'TrendChart',
  props: {
    title: {
      type: String,
      default: '趨勢圖'
    },
    data: {
      type: Array,
      required: true,
      default: () => []
    },
    xAxisKey: {
      type: String,
      default: 'date'
    },
    yAxisKey: {
      type: String,
      default: 'value'
    },
    width: {
      type: String,
      default: '100%'
    },
    height: {
      type: String,
      default: '300px'
    },
    color: {
      type: String,
      default: '#4facfe'
    },
    smooth: {
      type: Boolean,
      default: true
    },
    areaStyle: {
      type: Boolean,
      default: true
    }
  },
  setup(props) {
    const chartRef = ref(null);
    let chartInstance = null;

    // 格式化日期：YYYY-MM 維持月標籤；其餘轉 YYYY-MM-DD
    const formatDate = (dateString) => {
      if (!dateString) return '';
      if (typeof dateString === 'string' && /^\d{4}-\d{2}$/.test(dateString)) {
        return dateString.replace('-', '/');
      }
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const initChart = () => {
      if (!chartRef.value) return;

      // 初始化圖表
      chartInstance = echarts.init(chartRef.value);

      // 準備數據 - 格式化日期
      const xAxisData = props.data.map(item => {
        const value = item[props.xAxisKey];
        return props.xAxisKey === 'date' ? formatDate(value) : value;
      });
      const seriesData = props.data.map(item => item[props.yAxisKey]);

      // 設定選項
      const option = {
        title: {
          text: props.title,
          left: 'center',
          textStyle: {
            fontSize: 16,
            fontWeight: 500,
            color: '#333'
          }
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            label: {
              backgroundColor: '#6a7985'
            }
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: xAxisData,
          axisLabel: {
            rotate: 45,
            interval: 'auto'
          }
        },
        yAxis: {
          type: 'value'
        },
        series: [
          {
            name: props.title,
            type: 'line',
            smooth: props.smooth,
            lineStyle: {
              color: props.color,
              width: 2
            },
            areaStyle: props.areaStyle ? {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: `${props.color}80` },
                { offset: 1, color: `${props.color}10` }
              ])
            } : undefined,
            itemStyle: {
              color: props.color
            },
            data: seriesData
          }
        ]
      };

      chartInstance.setOption(option);
    };

    const updateChart = () => {
      if (!chartInstance) return;

      const xAxisData = props.data.map(item => {
        const value = item[props.xAxisKey];
        return props.xAxisKey === 'date' ? formatDate(value) : value;
      });
      const seriesData = props.data.map(item => item[props.yAxisKey]);

      chartInstance.setOption({
        xAxis: {
          data: xAxisData
        },
        series: [{
          data: seriesData
        }]
      });
    };

    const resizeChart = () => {
      if (chartInstance) {
        chartInstance.resize();
      }
    };

    onMounted(() => {
      initChart();
      window.addEventListener('resize', resizeChart);
    });

    watch(() => props.data, () => {
      updateChart();
    }, { deep: true });

    onBeforeUnmount(() => {
      if (chartInstance) {
        chartInstance.dispose();
        chartInstance = null;
      }
      window.removeEventListener('resize', resizeChart);
    });

    return {
      chartRef
    };
  }
};
</script>

<style scoped>
/* 無需額外樣式，寬高由 props 控制 */
</style>
